'use client';

import React, { Suspense } from 'react';
import { useRouter } from 'next/navigation';
import { SURFACE_DEFAULT_DATE_FILTER } from '@/constants/feed-filters';
import {
  BRAND_NEWS_FEED_FILTERS,
  urlStateToNewsFilters,
} from '@/constants/home-news-feed';

import { Brand } from '@repo/types';

import type { ArticlesFeedArray } from '@/hooks/api/article/model';
import {
  ArticleFeed,
  FeedPageContainer,
  HomeFeedFilterBar,
  useHomeFeedUrlState,
} from '@/components/feed';

interface ShowBrandPageProps {
  brand: Brand;
  initialArticles?: ArticlesFeedArray | null;
}

export const ShowBrandPage: React.FC<ShowBrandPageProps> = ({
  brand,
  initialArticles,
}) => (
  <FeedPageContainer>
    <Suspense fallback={null}>
      <BrandArticlesFeed brand={brand} initialArticles={initialArticles} />
    </Suspense>
  </FeedPageContainer>
);

const BrandArticlesFeed: React.FC<{
  brand: Brand;
  initialArticles?: ArticlesFeedArray | null;
}> = ({ brand, initialArticles }) => {
  const router = useRouter();
  // Brand surface default is the fixed This week window (invariant 10, Aki
  // veto): the SSR seed, the resolved client default, and the date dropdown
  // all read the same map entry, so the dropdown always shows the window the
  // feed actually fetched.
  const { filters, setFilters } = useHomeFeedUrlState(
    'all',
    SURFACE_DEFAULT_DATE_FILTER.brand,
  );
  const newsFilters = urlStateToNewsFilters(filters);

  // Only a real refinement (category, or a non-default date window) counts as
  // an active filter. At the surface default an empty feed renders the plain
  // empty state, not a clear-all tile that would no-op.
  const filtersActive =
    (filters.categories?.length ?? 0) > 0 ||
    newsFilters.dateFilter !== SURFACE_DEFAULT_DATE_FILTER.brand;

  return (
    <>
      <HomeFeedFilterBar
        filters={{ ...filters, dateFilter: newsFilters.dateFilter }}
        onFiltersChange={setFilters}
        showKinds={false}
        capDateOptions
        scopeBadge={{
          label: brand.brand,
          onRemove: () => router.push('/articles'),
        }}
      />
      <ArticleFeed
        filters={newsFilters}
        brandIds={[brand.id]}
        initialArticles={initialArticles}
        defaultFilters={BRAND_NEWS_FEED_FILTERS}
        // CRE-980: the brand page ships a SCORE-STRIPPED SSR seed (no
        // credibility/bias in the flight). Mark the seed stale so the client
        // feed query refetches `/articles/feed` on mount and fills the card
        // gauges in-app — one request, all cards, scores never in the flight.
        seedScoresStripped
        loginWallSurface="brand"
        filtersActive={filtersActive}
        alwaysShowClearAll
        // Clearing filters resets to THIS surface's default (This week) and
        // stays on the brand page; leaving the page is the scope badge's job,
        // not the clear-all tile's.
        onClearAll={() => setFilters({})}
      />
    </>
  );
};
